import { useEffect } from 'react';

/**
 * Custom React hook to handle deselection of events in a scheduler component when clicking outside.
 *
 * Adds a global click listener that clears the selected events if the user clicks outside the scheduler or not on an event/appointment element.
 *
 * @param {Object} params
 * @param {React.RefObject} params.schedulerContainerRef - Ref to the scheduler container DOM node.
 * @param {Function} params.setSelectedEvents - State setter for selected events.
 * @param {Function} [params.onSelectionChanged] - Optional callback called with an empty array when selection is cleared.
 */
function useEventSelectionChanged({
  schedulerContainerRef,
  setSelectedEvents,
  onSelectionChanged,
}) {
  useEffect(() => {
    function handleDocumentClick(e) {
      if (!schedulerContainerRef.current) return;

      // If the click is inside the scheduler and on an event, do not clear selection
      if (
        schedulerContainerRef.current.contains(e.target) &&
        e.target.closest('.e-appointment')
      ) {
        return;
      }

      // If the click is on a context menu (right-click menu) or menu item, do not clear selection
      if (e.target.closest('.e-contextmenu')) return;
      if (e.target.closest('.e-menu-item')) return;

      setSelectedEvents((prev) => {
        if (prev.length > 0) {
          if (onSelectionChanged) {
            onSelectionChanged({
              data: []
            });
          }
          return [];
        }
        return prev;
      });
    }

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [schedulerContainerRef, setSelectedEvents, onSelectionChanged]);
}

export default useEventSelectionChanged;
